import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from '../components/ui/Button'
import ConfirmModal from '../components/ui/ConfirmModal'
import { getChains } from '../services/chainsService'
import { clearHistory, getHistory } from '../services/scanHistory'
import { getDisabledRetailers, setRetailerEnabled } from '../services/retailerPrefs'
import { prettyChain } from '../lib/chains'
import { isAnalyticsOptedOut, setAnalyticsOptOut } from '../services/analytics'
import { SUPPORTED_LANGUAGES } from '../i18n'

// Endonyms — each language shown in its own name, regardless of the active locale.
const LANGUAGE_NAMES = { hr: 'Hrvatski', en: 'English', de: 'Deutsch', it: 'Italiano' }

export default function SettingsPage() {
  const { t, i18n } = useTranslation()
  const [chains, setChains] = useState([])
  const [chainsStatus, setChainsStatus] = useState('loading') // loading | loaded | error
  const [disabled, setDisabled] = useState(() => getDisabledRetailers())
  const [historyCount, setHistoryCount] = useState(() => getHistory().length)
  const [historyCleared, setHistoryCleared] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [optOutChanged, setOptOutChanged] = useState(false)

  useEffect(() => {
    let active = true
    getChains()
      .then((list) => {
        if (!active) return
        setChains(list)
        setChainsStatus('loaded')
      })
      .catch(() => {
        if (active) setChainsStatus('error')
      })
    return () => {
      active = false
    }
  }, [])

  function toggleRetailer(chain) {
    const enabled = disabled.has(chain) // currently disabled → we're enabling it
    setRetailerEnabled(chain, enabled)
    setDisabled(getDisabledRetailers())
  }

  function enableAll() {
    chains.forEach((chain) => setRetailerEnabled(chain, true))
    setDisabled(getDisabledRetailers())
  }

  function disableAll() {
    chains.forEach((chain) => setRetailerEnabled(chain, false))
    setDisabled(getDisabledRetailers())
  }

  function handleClearRequest() {
    setShowConfirm(true)
  }

  function handleConfirmClear() {
    clearHistory()
    setHistoryCount(0)
    setHistoryCleared(true)
    setShowConfirm(false)
  }

  function handleCancelClear() {
    setShowConfirm(false)
  }

  function handleExportCsv() {
    const history = getHistory()
    if (history.length === 0) return

    const escapeField = (val) => {
      const str = String(val ?? '')
      if (str.includes(';') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }

    const formatPrice = (price) => {
      if (price === null || price === undefined || Number.isNaN(Number(price))) return ''
      return Number(price).toFixed(2).replace('.', ',')
    }

    const formatDate = (isoString) => {
      if (!isoString) return ''
      const d = new Date(isoString)
      if (Number.isNaN(d.getTime())) return ''
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}.`
    }

    const header = t('settings.csvHeader')
    const rows = history.map((item) =>
      [
        escapeField(item.name),
        escapeField(item.ean),
        escapeField(item.brand),
        escapeField(item.quantity),
        escapeField(item.unit),
        escapeField(formatDate(item.scannedAt)),
        escapeField(formatPrice(item.cheapestPrice)),
      ].join(';')
    )

    const bom = '﻿'
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'povijest-skeniranja.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <main id="main-content" className="max-w-md mx-auto px-4 py-10">
      <Link to="/" className="min-h-[48px] inline-flex items-center px-2 mb-6 text-primary-text hover:underline">
        ← {t('settings.back')}
      </Link>

      <h1 className="text-2xl font-bold text-fg">{t('settings.title')}</h1>

      <section aria-label={t('settings.languageTitle')} className="mt-6 bg-surface rounded-card shadow-card p-4">
        <h2 className="text-lg font-semibold text-fg">{t('settings.languageTitle')}</h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {SUPPORTED_LANGUAGES.map((lng) => {
            const active = i18n.resolvedLanguage === lng
            return (
              <button
                key={lng}
                type="button"
                lang={lng}
                onClick={() => i18n.changeLanguage(lng)}
                aria-pressed={active}
                className={`rounded-btn px-3 py-3 text-base font-medium border transition-colors min-h-[48px] ${
                  active
                    ? 'bg-primary text-on-primary border-primary'
                    : 'bg-surface text-fg border-line hover:bg-surface-2'
                }`}
              >
                {LANGUAGE_NAMES[lng]}
              </button>
            )
          })}
        </div>
      </section>

      <section aria-label={t('settings.historyTitle')} className="mt-6 bg-surface rounded-card shadow-card p-4">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-fg">{t('settings.historyTitle')}</h2>
          <span className="text-sm text-muted shrink-0">
            {historyCleared ? t('settings.cleared') : t('settings.productCount', { count: historyCount })}
          </span>
        </div>
        <p className="mt-1 text-base text-muted">
          {t('settings.historyNote')}
        </p>
        <div className="mt-3 space-y-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleExportCsv}
            disabled={historyCount === 0}
            className="w-full"
          >
            {t('settings.exportCsv')}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={handleClearRequest}
            disabled={historyCount === 0}
            className="w-full"
          >
            {t('settings.clearHistory')}
          </Button>
        </div>
      </section>

      <section aria-label={t('settings.storesTitle')} className="mt-6 bg-surface rounded-card shadow-card p-4">
        <h2 className="text-lg font-semibold text-fg">{t('settings.storesTitle')}</h2>
        <p className="mt-1 text-base text-muted">
          {t('settings.storesDesc')}
        </p>

        {chainsStatus === 'loading' && <p className="mt-3 text-base text-muted">{t('settings.storesLoading')}</p>}

        {chainsStatus === 'error' && (
          <p role="alert" className="mt-3 text-base text-warn-fg">
            {t('settings.storesError')}
          </p>
        )}

        {chainsStatus === 'loaded' && (
          <>
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={enableAll} className="border border-line">
                {t('settings.selectAll')}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={disableAll} className="border border-line">
                {t('settings.deselectAll')}
              </Button>
            </div>
            <ul className="mt-3 divide-y divide-line">
              {chains.map((chain) => {
                const enabled = !disabled.has(chain)
                return (
                  <li key={chain} className="flex items-center justify-between py-3 min-h-[48px]">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={`/retailers/${chain}.jpg`}
                        alt=""
                        loading="lazy"
                        className="h-8 w-8 rounded object-contain bg-surface-2 shrink-0"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      <span className="text-fg truncate">{prettyChain(chain)}</span>
                    </div>
                    <label className="inline-flex items-center cursor-pointer shrink-0 ml-3">
                      <span className="sr-only">{prettyChain(chain)}</span>
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => toggleRetailer(chain)}
                        className="h-5 w-5 rounded border-line text-primary focus:ring-primary"
                      />
                    </label>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </section>

      <section aria-label={t('settings.analyticsTitle')} className="mt-6 bg-surface rounded-card shadow-card p-4">
        <h2 className="text-lg font-semibold text-fg">{t('settings.analyticsTitle')}</h2>
        <p className="mt-1 text-base text-muted">
          {t('settings.analyticsDesc')}
          {!isAnalyticsOptedOut() && ' ' + t('settings.analyticsDescExtra')}
        </p>
        <div className="mt-3">
          <label className="inline-flex items-center cursor-pointer gap-2">
            <input
              type="checkbox"
              checked={!isAnalyticsOptedOut()}
              onChange={(e) => {
                setAnalyticsOptOut(!e.target.checked)
                setOptOutChanged(true)
              }}
              className="h-5 w-5 rounded border-line text-primary focus:ring-primary"
            />
            <span className="text-base text-fg">{t('settings.analyticsAllow')}</span>
          </label>
          {optOutChanged && !isAnalyticsOptedOut() && (
            <p className="mt-2 text-base text-success-fg">{t('settings.analyticsReenabled')}</p>
          )}
          {optOutChanged && isAnalyticsOptedOut() && (
            <p className="mt-2 text-base text-warn-fg">{t('settings.analyticsDisabled')}</p>
          )}
        </div>
      </section>
      <ConfirmModal
        isOpen={showConfirm}
        title={t('settings.confirmClearTitle')}
        body={t('settings.confirmClearBody')}
        confirmLabel={t('settings.confirmDelete')}
        cancelLabel={t('settings.cancel')}
        onConfirm={handleConfirmClear}
        onCancel={handleCancelClear}
        variant="danger"
      />
    </main>
  )
}
